<template>
	<div>
		<h1 class="mb-4">{{ title }}</h1>
		<v-filters
			class="mb-4"
			v-bind="{
				filters: urlFilters,
				loading
			}"
			@update-filters="updateUrl"
		/>
		<v-select
			v-model="tariffFilter"
			label="Тариф клиента"
			:items=clientsTariffs
			style="max-width: 250px"
			dense
			clearable
		/>
		<v-tabs
			:key="urlFilters.source"
			:value="selectTabValue"
			class="mb-1"
			grow
		>
			<v-tab
				v-for="item of OPTIONS_SOURCE"
				:key="item.id"
				@change="updateUrl({source: item.id})"
			>
				<span>{{ item.text }}</span>
			</v-tab>
		</v-tabs>
		<v-data-table
			class="sessions-table"
			:headers="headers"
			:items="filteredUpdates"
			:loading="loading.table"
			:server-items-length="updatesSaved.length"
			hide-default-footer
			disable-pagination
			@update:sort-by="updateSortBy"
			@update:sort-desc="updateSortDesc"
		>
			<template v-slot:item.number="{ item, index }">
				<span v-if="!item.total">{{ index }}</span>
			</template>
			<template v-slot:item.client_id="{ item }">
				<span :class="{ 'font-weight-bold': (item.total) }">
					<sup>{{ item.client_id }}</sup>
					{{ item.total ? 'Total' : findClientName(item.client_id) }}
				</span>
				<span>
					<div class="text-caption grey--text">
					{{ findClientTariff(item.client_id) }}
					</div>
				</span>
			</template>
			<template v-slot:item.project_id="{ item,index }">
				<div :class="{'d-flex justify-space-between': index !== 0}">
					<span :class="{'font-weight-bold': (item.total)}"><sup v-if="!item.total">{{ item.project_id }}</sup> {{item.total ? item.total_projects  : item.project_name}}</span>
					<a
						v-if="!item.total"
						:href="generateLink(item)"
						target="_blank"
					>
					<v-icon small color="gray">mdi-link</v-icon>
					</a>
				</div>
			</template>
			<template v-slot:item.host_name="{ item }">
				<span :class="{'font-weight-bold': (item.total)}">{{ item.host_name }}</span>
			</template>
			<template v-slot:item.date="{ item }">
				<span class="text-no-wrap">{{ item.date }}</span>
			</template>
			<template v-slot:item.active_queries_cnt="{ item }">
				<span v-if="item.active_queries_cnt !== undefined && item.active_queries_cnt !== null" :class="{'font-weight-bold': (item.total)}">
					{{ item.active_queries_cnt.toLocaleString() }}
				</span>
				<span v-else>-</span>
			</template>
			<template v-slot:item.processed_queries_cnt="{ item }">
				<span v-if="item.processed_queries_cnt !== undefined && item.processed_queries_cnt !== null" :class="{'font-weight-bold': (item.total)}">
					{{ item.processed_queries_cnt.toLocaleString() }}
				</span>
				<span v-else>-</span>
				<span
					:class="{ 'font-weight-bold': (item.total) }"
				>
					<div class="text-caption grey--text">
					{{ item.host_name }}
					</div>
				</span>
			</template>
			<template v-slot:item.ch_saved_prc="{ item }">
				<strong
					v-if="item.ch_saved_prc !== undefined && item.ch_saved_prc !== null"
				>
					{{ item.ch_saved_prc.toLocaleString({ maximumFractionDigits: 3 }) }}%
				</strong>
				<span v-else>-</span>
			</template>
			<template v-slot:item.is_import="{ item }">
				<span>{{ item.is_import }}</span>
			</template>
			<template v-slot:item.vendors="{ item }">
				<span>{{ Array.isArray(item.vendors) ? item.vendors.join(', ') : '' }}</span>
			</template>
			<template v-slot:item.is_failed="{ item }">
				<span :class="{'font-weight-bold': (item.total)}">{{ item.is_failed.toLocaleString() }}</span>
			</template>
			<template v-slot:item.ws.ws_volume.cnt.ws1="{ item }">
				<span :class="{'font-weight-bold': (item.total)}">{{ item.total ?  item.total_ws1_volume_cnt.toLocaleString() : item.ws.ws_volume.cnt.ws1.toLocaleString() }}</span>
			</template>
			<template v-slot:item.ws.ws_volume.cnt.ws2="{ item }">
				<span :class="{'font-weight-bold': (item.total)}">{{ item.total ?  item.total_ws2_volume_cnt.toLocaleString() : item.ws.ws_volume.cnt.ws2.toLocaleString() }}</span>
			</template>
			<template v-slot:item.ws.ws_volume.prc.ws1="{ item }">
				<span :class="{'font-weight-bold': (item.total)}">{{ item.total ?  item.avg_ws1_volume_prc.toLocaleString() : item.ws.ws_volume.prc.ws1.toLocaleString() }}%</span>
			</template>
			<template v-slot:item.ws.ws_volume.prc.ws2="{ item }">
				<span :class="{'font-weight-bold': (item.total)}">{{ item.total ?  item.avg_ws2_volume_prc.toLocaleString() : item.ws.ws_volume.prc.ws2.toLocaleString() }}%</span>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { isEqual } from 'lodash'
import { NOTIFY_STATUS, OPTIONS_SOURCE } from '@/const'
import errors from '@/store/errors'
import { validateFilterFromOptions } from '@/function/validateFilter'

import updateUrlMixin from '@/mixins/updateUrlMixin'
import filters from './components/filters.vue'

const SORTING_AVAILABLE = [
	'date',
	'client_id',
	'project_id',
	'ch_saved_prc',
	'active_queries_cnt',
	'processed_queries_cnt',
	'ws1_volume_cnt',
	'ws2_volume_cnt',
	'ws1_volume_prc',
	'ws2_volume_prc',
	'vendors'
]

const CLIENTS_TARIFFS = [
	{ text: 'Все', value: 'all' },
	{ text: 'Не выбран', value: 'Не выбран' },
	{ text: 'Base', value: 'Base' },
	{ text: 'Pro', value: 'Pro' },
	{ text: 'Premium', value: 'Premium' },
	{ text: 'Специальный', value: 'Специальный' },
	{ text: 'Enterprise', value: 'Enterprise' },
	{ text: 'Demo', value: 'Demo' },
	{ text: 'Position', value: 'Position' },
	{ text: 'Position Plus', value: 'Position Plus' }
]

const NEED_TO_UPDATE_NAMES = [
	'ws.ws_volume.cnt.ws1',
	'ws.ws_volume.cnt.ws2',
	'ws.ws_volume.prc.ws1',
	'ws.ws_volume.prc.ws2'
]

export default {
	name: 'saved',
	metaInfo () {
		return {
			title: this.title
		}
	},
	mixins: [updateUrlMixin],
	components: {
		'v-filters': filters
	},
	data () {
		return {
			tariffFilter: 'all',
			clientsTariffs: CLIENTS_TARIFFS,
			clientSearch: '',
			title: 'Объемы сборов по проектам ch-proxy',
			headers: [
				{
					text: '№',
					align: 'center',
					divider: true,
					value: 'number'
				},
				{
					text: 'Клиент',
					value: 'client_id',
					align: 'center',
					divider: true
				},
				{
					text: 'Проект',
					value: 'project_id',
					align: 'center',
					divider: true
				},
				{
					text: 'Дата',
					value: 'date',
					align: 'center',
					divider: true
				},
				{
					text: 'Кол-во активных запросов',
					value: 'active_queries_cnt',
					align: 'center',
					divider: true
				},
				{
					text: 'Кол-во обработанных запросов',
					value: 'processed_queries_cnt',
					align: 'center',
					divider: true
				},
				{
					text: '% сохранено',
					value: 'ch_saved_prc',
					align: 'center',
					divider: true
				},
				{
					text: 'is_import',
					value: 'is_import',
					divider: true,
					sortable: false
				},
				{
					text: 'vendors',
					value: 'vendors',
					divider: true,
					sortable: true
				},
				{
					text: 'is_failed',
					value: 'is_failed',
					divider: true,
					sortable: false
				},
				{
					text: 'Частотность ws1',
					value: 'ws.ws_volume.cnt.ws1',
					divider: true
				},
				{
					text: 'Частотность ws2',
					value: 'ws.ws_volume.cnt.ws2',
					divider: true
				},
				{
					text: '% сбора частотности ws1',
					value: 'ws.ws_volume.prc.ws1',
					divider: true
				},
				{
					text: '% сбора частотности ws2',
					value: 'ws.ws_volume.prc.ws2',
					divider: true
				}
			],
			loading: {
				clients: true,
				shards: true,
				table: true
			},
			OPTIONS_SOURCE
		}
	},
	computed: {
		...mapState({
			clients: state => state.clients,
			projects: state => state.options.projects,
			shards: state => state.updates.shards,
			vendors: state => state.updates.vendors,
			hosts: state => state.updates.hosts,
			updatesSaved: state => state.updates.updatesSaved,
			updatesSavedTotals: state => state.updates.updatesSavedTotals
		}),
		selectTabValue () {
			return this.OPTIONS_SOURCE.findIndex(item => item.id === this.urlFilters.source)
		},
		urlFilters () {
			const {
				date_from,
				date_to,
				client_id,
				project_id,
				source,
				shard_name,
				vendors,
				host_name,
				only_failed,
				hide_null_data,
				hide_import,
				sort
			} = this.$route.query

			const today = new Date().toISOString().split('T')[0]

			const sortName = sort ? String(sort).replace('-', '') : 'ch_saved_prc'
			const sortNameValidated = validateFilterFromOptions(sortName, SORTING_AVAILABLE, 'ch_saved_prc')
			const sortWithDirection = String(sort).startsWith('-') ? `-${sortNameValidated}` : sortNameValidated
			const projectId = Array.isArray(project_id) ? project_id : [project_id]

			return {
				dateFrom: date_to && date_from ? date_from : today,
				dateTo: date_to && date_from ? date_to : today,
				clientId: validateFilterFromOptions(Array.isArray(client_id) ? client_id : [client_id], this.clients, [], true, true),
				projectId: projectId
					.map(item => +item)
					.filter(item => !isNaN(item)),
				source: validateFilterFromOptions(source, OPTIONS_SOURCE, 'yandex'),
				shardName: validateFilterFromOptions(shard_name, this.shards, [], true),
				vendors: !vendors || (typeof vendors === 'string' && !vendors.trim()) ? null : Array.isArray(vendors) ? vendors : [vendors],
				hostName: validateFilterFromOptions(host_name, this.hosts, [], true),
				onlyFailed: String(only_failed) === 'true' ? 1 : 0,
				hideNullData: String(hide_null_data) === 'true' ? 1 : 0,
				hideImport: String(hide_import) === 'true' ? 1 : 0,
				sort: [sortWithDirection]
			}
		},
		updatesSavedAndTotal () {
			const firstRow = {
				total_projects: this.updatesSavedTotals.total_rows_cnt,
				active_queries_cnt: this.updatesSavedTotals.total_active_queries_cnt,
				processed_queries_cnt: this.updatesSavedTotals.total_processed_queries_cnt,
				ch_saved_prc: this.updatesSavedTotals.avg_ch_saved_prc,
				is_failed: this.updatesSavedTotals.total_failed_cnt,
				total: true,
				total_ws1_volume_cnt: this.updatesSavedTotals.total_ws1_volume_cnt,
				total_ws2_volume_cnt: this.updatesSavedTotals.total_ws2_volume_cnt,
				avg_ws1_volume_prc: this.updatesSavedTotals.avg_ws1_volume_prc,
				avg_ws2_volume_prc: this.updatesSavedTotals.avg_ws2_volume_prc
			}
			return [firstRow, ...this.updatesSaved]
		},
		filteredUpdates () {
			if (!this.tariffFilter || this.tariffFilter === 'all') {
				return this.updatesSavedAndTotal
			}

			return this.updatesSavedAndTotal.filter(item => {
				if (item.total) return false
				return this.findClientTariff(item.client_id) === this.tariffFilter
			})
		}

	},
	async created () {
		try {
			await Promise.all([
				this.getClients({ include_tariffs: 1 }),
				this.initDataShardsAndHosts()
			])
		} catch (_) {
			this.$notify({
				type: NOTIFY_STATUS.ERROR,
				title: errors.GET_DATA
			})
		} finally {
			this.loading.clients = false
		}
		await this.initDataTable()
	},
	watch: {
		async '$route.query' (value, oldValue) {
			const filtersShards = [
				'date_from',
				'date_to',
				'client_id',
				'project_id',
				'source'
			]
			try {
				if (filtersShards.some(filter => !isEqual(value[filter], oldValue[filter]))) {
					await this.initDataShardsAndHosts()
				}
				this.initDataTable()
			} catch (_) {
				this.$notify({
					type: NOTIFY_STATUS.ERROR,
					title: errors.GET_DATA
				})
			}
		}
	},
	methods: {
		...mapActions({
			getClients: 'getClients',
			getUpdatesSaved: 'updates/getUpdatesSaved',
			getUpdatesShards: 'updates/getUpdatesShards'
		}),
		async initDataTable () {
			const {
				dateFrom,
				dateTo,
				clientId,
				projectId,
				source,
				shardName,
				vendors,
				hostName,
				onlyFailed,
				hideNullData,
				hideImport,
				sort
			} = this.urlFilters

			this.loading.table = true
			await this.getUpdatesSaved({
				query: {
					date_from: dateFrom,
					date_to: dateTo,
					client_ids: clientId,
					project_ids: projectId,
					source: source,
					shard_names: shardName,
					host_names: hostName,
					vendors,
					only_failed: onlyFailed,
					hide_null_data: hideNullData,
					hide_import: hideImport,
					sort
				}
			})
			this.loading.table = false
		},
		async initDataShardsAndHosts () {
			const {
				dateFrom,
				dateTo,
				clientId,
				projectId,
				source
			} = this.urlFilters

			this.loading.shards = true
			await this.getUpdatesShards({
				query: {
					date_from: dateFrom,
					date_to: dateTo,
					client_ids: clientId.length || projectId.length ? clientId : ['00'],
					project_ids: projectId,
					source: source
				}
			})
			this.loading.shards = false
		},
		updatedNames (name) {
			switch (name) {
			case 'ws.ws_volume.cnt.ws1':
				return 'ws1_volume_cnt'
			case 'ws.ws_volume.cnt.ws2':
				return 'ws2_volume_cnt'
			case 'ws.ws_volume.prc.ws1':
				return 'ws1_volume_prc'
			case 'ws.ws_volume.prc.ws2':
				return 'ws2_volume_prc'
			}
		},
		updateSortBy (name) {
			if (NEED_TO_UPDATE_NAMES.includes(name)) {
				const sortName = this.updatedNames(name)
				return this.updateUrl({ sort: sortName })
			}
			this.updateUrl({ sort: name })
		},
		updateSortDesc (value) {
			if (value !== undefined) {
				const sortName = this.urlFilters.sort[0].replace('-', '')
				this.updateUrl({ sort: `${value ? '-' : ''}${sortName}` })
			}
		},
		findClientName (id) {
			if (this.clients && Array.isArray(this.clients)) {
				const client = this.clients.find((el) => +el.id === +id)
				return client && client.name ? client.name : ''
			}
			return ''
		},
		findClientTariff (id) {
			if (this.clients && Array.isArray(this.clients)) {
				const client = this.clients.find((el) => +el.id === +id)
				return client && client.tariff ? client.tariff : ''
			}
			return ''
		},
		generateLink (item) {
			if (item) {
				return `https://my.seowork.ru/semantics/dashboard/${item.project_id}?date_from=${item.date}&date_to=${item.date}`
			}
			return null
		}
	}
}
</script>
